import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend,
} from 'recharts';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import EmptyState from '../../components/crm/EmptyState';
import { fmtCents } from '../../lib/financial';
import { useConsoleTheme } from '../../lib/theme';

// Recharts takes colours as props, so the light theme (docs/light-mode.md §3)
// is chosen here. Dark values are the originals, unchanged. Light: axis text
// ink-3, grid hair, lime series → accent text (#41680D, also readable as axis
// tick text), emerald series → emerald-700, tooltip on a raised white card.
const CHART_DARK = {
  axis: '#888',
  grid: 'rgba(255,255,255,0.05)',
  lime: '#B0FC38',
  emerald: '#34d399',
  tooltip: { background: '#0a0a0f', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 },
};
const CHART_LIGHT = {
  axis: '#5C584C',
  grid: 'rgba(28,27,22,0.1)',
  lime: '#41680D',
  emerald: '#047857',
  tooltip: { background: '#FFFFFF', border: '1px solid rgba(28,27,22,0.14)', borderRadius: 8, fontSize: 12, color: '#1C1B16' },
};
function useChartColors() {
  const { isLight } = useConsoleTheme();
  return isLight ? CHART_LIGHT : CHART_DARK;
}

export default function Analytics() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'nexsav_admin';
  const isStaff = isAdmin || user?.role === 'ops';

  const [pipeline, setPipeline] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [auditsAnalytics, setAuditsAnalytics] = useState(null);
  const [clientHealth, setClientHealth] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [errors, setErrors] = useState({});

  const load = useCallback(async () => {
    const fetches = [
      api.get('/api/admin/analytics/pipeline').then(setPipeline).catch((e) => setErrors((x) => ({ ...x, pipeline: e.message }))),
      api.get('/api/admin/analytics/audits').then(setAuditsAnalytics).catch((e) => setErrors((x) => ({ ...x, audits: e.message }))),
      api.get('/api/admin/analytics/client-health').then(setClientHealth).catch((e) => setErrors((x) => ({ ...x, health: e.message }))),
    ];
    if (isAdmin) {
      fetches.push(api.get('/api/admin/analytics/revenue').then(setRevenue).catch((e) => setErrors((x) => ({ ...x, revenue: e.message }))));
    }
    await Promise.all(fetches);
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  if (!isStaff) return <EmptyState title="Staff only" />;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight"><span className="serif-acc accent-text">Analytics</span></h1>
        <p className="text-[13px] text-gray-500 mt-0.5">Operational + revenue dashboards.</p>
      </div>

      {isAdmin && revenue && <RevenueSection data={revenue} />}
      {pipeline && <PipelineSection data={pipeline} />}
      {auditsAnalytics && <AuditsSection data={auditsAnalytics} />}
      {clientHealth && <ClientHealthSection data={clientHealth} isAdmin={isAdmin} />}
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-[14px] font-semibold text-white">{title}</h2>
          {subtitle && <div className="text-[11px] text-gray-500 mt-0.5">{subtitle}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

function RevenueSection({ data }) {
  const pal = useChartColors();
  // Build MoM booked + collected, sorted ASC for chart
  const monthly = {};
  for (const r of data.bookedByMonth || []) monthly[r.month] = { month: r.month, booked: r.cents / 100 };
  for (const r of data.collectedByMonth || []) {
    monthly[r.month] = { ...monthly[r.month], month: r.month, collected: r.cents / 100 };
  }
  const chart = Object.values(monthly).sort((a, b) => a.month.localeCompare(b.month));
  // Cumulative collected
  let running = 0;
  chart.forEach((p) => { running += (p.collected || 0); p.cumulative = running; });

  return (
    <Section title="Revenue" subtitle="Admin-only. Booked = invoiced (non-void/draft); Collected = payments received.">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <StatLine label="AR outstanding" value={fmtCents(data.outstandingAR?.outstandingCents)} to="/admin/financial?status=sent" testId="kpi-ar-outstanding" />
        <StatLine label="Billed (non-void)" value={fmtCents(data.outstandingAR?.billedCents)} to="/admin/financial" testId="kpi-ar-billed" />
        <StatLine label="Avg deal size" value={fmtCents(data.avgDealSizeCents)} to="/admin/financial?status=paid" testId="kpi-avg-deal" />
      </div>
      <div className="h-64" data-testid="revenue-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chart} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={pal.grid} />
            <XAxis dataKey="month" stroke={pal.axis} tick={{ fontSize: 11 }} />
            <YAxis stroke={pal.axis} tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
            <Tooltip contentStyle={pal.tooltip}
              formatter={(v, n) => [fmtCents(v * 100), n]} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="booked" fill={pal.lime} name="Booked" />
            <Bar dataKey="collected" fill={pal.emerald} name="Collected" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Section>
  );
}

function PipelineSection({ data }) {
  const pal = useChartColors();
  // Funnel: list of {stage, count} already in data.funnel
  const chart = (data.funnel || []).map((s) => ({ stage: s.stage, count: s.count }));
  return (
    <Section title="Pipeline funnel" subtitle="Accounts per stage (school accounts only).">
      <div className="h-56" data-testid="pipeline-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chart} layout="vertical" margin={{ top: 10, right: 10, left: 40, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={pal.grid} />
            <XAxis type="number" stroke={pal.axis} tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="stage" stroke={pal.axis} tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={pal.tooltip} />
            <Bar dataKey="count" fill={pal.lime} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4 text-[11px]">
        {(data.conversions || []).map((c) => (
          <div key={`${c.from}-${c.to}`} className="p-2 rounded-lg border border-white/[0.05] bg-white/[0.015]">
            <div className="text-gray-500">{c.from} → {c.to}</div>
            <div className="text-white font-medium">{c.rate != null ? `${(c.rate * 100).toFixed(0)}%` : '—'}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function AuditsSection({ data }) {
  const pal = useChartColors();
  const chart = (data.monthly || []).slice().reverse().map((m) => ({
    month: m.month,
    avgScore: m.avgScore ?? 0,
    passRate: m.passRate != null ? Math.round(m.passRate * 100) : 0,
  }));
  return (
    <Section title="Audits · pass rate trend"
      subtitle={`Threshold ${data.passThreshold}. ${data.totals.total} audits total, ${data.totals.passed} passed.`}>
      <div className="h-64" data-testid="audits-chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chart} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={pal.grid} />
            <XAxis dataKey="month" stroke={pal.axis} tick={{ fontSize: 11 }} />
            <YAxis yAxisId="score" stroke={pal.emerald} tick={{ fontSize: 11 }} domain={[0, 100]} />
            <YAxis yAxisId="rate" orientation="right" stroke={pal.lime} tick={{ fontSize: 11 }} domain={[0, 100]} unit="%" />
            <Tooltip contentStyle={pal.tooltip} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line yAxisId="score" type="monotone" dataKey="avgScore" stroke={pal.emerald} name="Avg score" strokeWidth={2} dot={{ r: 3 }} />
            <Line yAxisId="rate" type="monotone" dataKey="passRate" stroke={pal.lime} name="Pass %" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {data.regressions?.length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] text-red-300 uppercase tracking-wider mb-2">Regressions ({data.regressions.length})</div>
          <div className="space-y-1">
            {data.regressions.slice(0, 10).map((r) => (
              <div key={r.accountId} className="flex items-center justify-between p-2 rounded-lg border border-red-500/20 bg-red-500/5 text-[12px]">
                <span className="text-gray-300">{r.accountName || r.accountSlug}</span>
                <span className="text-red-300">−{r.drop} pts ({r.priorScore} → {r.latestScore})</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}

function ClientHealthSection({ data, isAdmin }) {
  return (
    <Section title="Client health"
      subtitle={`Composite score per live+maintained account. Lower = needs attention. ${data.passes?.length || 0} accounts.`}>
      <div className="rounded-xl border border-white/[0.05] overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-white/[0.02] border-b border-white/[0.05] text-[10px] uppercase tracking-wider text-gray-500">
          <div className="col-span-7 lg:col-span-3">Account</div>
          <div className="col-span-5 lg:col-span-2">Composite</div>
          <div className="hidden lg:block lg:col-span-2">Days since contact</div>
          <div className="hidden lg:block lg:col-span-2">Latest audit</div>
          {isAdmin && <div className="hidden lg:block lg:col-span-3 text-right">Outstanding</div>}
        </div>
        {(data.passes || []).slice(0, 30).map((r) => (
          <div key={r.accountId} data-testid="client-health-row"
            className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-white/[0.03] text-[12px]">
            <div className="col-span-7 lg:col-span-3 text-white truncate">{r.accountName}</div>
            <div className="col-span-5 lg:col-span-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] border ${healthTone(r.compositeHealth)}`}>
                {r.compositeHealth.toFixed(2)}
              </span>
            </div>
            <div className="hidden lg:block lg:col-span-2 text-gray-400">{r.daysSinceLastContact ?? '—'}</div>
            <div className="hidden lg:block lg:col-span-2 text-gray-400">{r.latestAuditScore ?? '—'}</div>
            {isAdmin && (
              <div className="hidden lg:block lg:col-span-3 text-right text-gray-400" data-testid="client-health-outstanding">
                {fmtCents(r.outstandingCents)}
              </div>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

function healthTone(v) {
  if (v >= 0.7) return 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20';
  if (v >= 0.4) return 'bg-amber-500/10 text-amber-300 border-amber-500/20';
  return 'bg-red-500/10 text-red-300 border-red-500/20';
}

function StatLine({ label, value, to, testId }) {
  // Sprint H item 9.3 — KPI cards become drill-down links when `to` is set.
  // Hover shows pointer + subtle underline.
  const inner = (
    <>
      <div className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</div>
      <div className="text-[18px] text-white font-semibold mt-0.5 group-hover:underline">{value}</div>
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        data-testid={testId}
        className="group block p-3 rounded-lg border border-white/[0.05] bg-white/[0.015] hover:bg-white/[0.04] transition-colors cursor-pointer"
      >
        {inner}
      </Link>
    );
  }
  return (
    <div data-testid={testId} className="p-3 rounded-lg border border-white/[0.05] bg-white/[0.015]">
      {inner}
    </div>
  );
}
